// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import BargainBinCategoryAccordion from "@awc/App/Components/BargainBin/BargainBinCategoryAccordion";
import BargainBinProductInfo from "@awc/App/Components/BargainBin/BargainBinProductInfo";
import Notify from "@awc/App/Components/Notify";
import loadBargainBinCategories from "@awc/App/Handlers/BargainBin/GetBargainBinCategories";
import loadBargainBinProducts from "@awc/App/Handlers/BargainBin/GetBargainProducts";
import { BargainBinCategoriesModel, BargainBinsCategoryModel } from "@awc/App/Services/BargainBin";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

export const enum BarginBinCategoriesLinks {
    Wire = "/bargain-bin/discount-wire-and-cable",
    Bulk = "/bargain-bin/bulk-deals",
    Tubing = "AddToCartActibargain-bin/discount-tubingons",
}

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const { productId } = getQueryStrings(state);
    const selectedProductId = state.xBargain.selectedProductId;
    return {
        productId,
        isLoadingCategories: state.xBargain.isLoadingCategories,
        isProductLoading: state.xBargain.isProductLoading,
        isLoadingBargains: state.xBargain.isLoadingBargains,
        selectedProduct: getProductStateByPath(state, selectedProductId).value,
        productNotFound: state.xBargain.productNotFound,
    };
};

const mapDispatchToProps = {
    loadBargainBinCategories,
    loadBargainBinProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BargainBinStyles {
    wrapper?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    accordionsWrapper?: InjectableCss;
}

export const bargainBinStyles: BargainBinStyles = {};

const styles = bargainBinStyles;

const BargainBin = ({
    loadBargainBinCategories,
    isLoadingCategories,
    isProductLoading,
    isLoadingBargains,
    selectedProduct,
    productNotFound,
}: Props) => {
    const [bargainBinCategories, setBargainBinCategories] = useState<BargainBinsCategoryModel[]>([]);
    const toaster = useContext(ToasterContext);
    const dispatch = useDispatch();

    useEffect(() => {
        if (bargainBinCategories.length || isLoadingCategories) {
            return;
        }
        getCategoryNameAndCallApi();

        return () => {
            dispatch({
                type: "xBargain/ResetProductNotFound",
            } as any);
        };
    }, []);

    useEffect(() => {
        if (productNotFound) {
            toaster.addToast({
                body: translate("Product not Found."),
                messageType: "danger",
            });
        }
    }, [productNotFound]);

    // add selectedProductId to the url
    useEffect(() => {
        selectedProduct && updateUrl(selectedProduct.id);
    }, [selectedProduct]);

    const updateUrl = (id: string) => {
        const currentUrl = window.location.href;
        const updatedUrl = `${currentUrl.split("?")[0]}?productId=${id}`;
        window.history.replaceState({}, document.title, updatedUrl);
    };

    const getCategoryNameAndCallApi = () => {
        const urlSegment = window.location.pathname.split("?")[0];
        let bargainBinCategory;
        switch (urlSegment) {
            case BarginBinCategoriesLinks.Wire:
                bargainBinCategory = "wire";
                break;
            case BarginBinCategoriesLinks.Tubing:
                bargainBinCategory = "tubing";
                break;

            case BarginBinCategoriesLinks.Bulk:
                bargainBinCategory = "bulk";
                break;
            default:
                bargainBinCategory = "";
                break;
        }
        getBargainBinCategories(bargainBinCategory);
    };

    const getBargainBinCategories = (bargainBinCategory: string) => {
        loadBargainBinCategories({
            bargainBinCategory,

            onSuccess: (resultProps: ServiceResult<BargainBinCategoriesModel>) => {
                setBargainBinCategories((resultProps as any)?.result?.bargainBinCategories);
                if ((resultProps as any)?.result?.bargainBinCategories.length === 0) {
                    toaster.addToast({
                        body: translate("Categories not Found."),
                        messageType: "danger",
                    });
                }
            },
            onError: (error: string) => {
                toaster.addToast({
                    body: translate("Error loading Categories."),
                    messageType: "danger",
                });
            },
        });
    };

    return (
        <LoadingOverlay loading={isLoadingCategories}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.leftSection}>
                    {!!bargainBinCategories?.length && (
                        <StyledWrapper {...styles.accordionsWrapper}>
                            {bargainBinCategories?.map(category => (
                                <BargainBinCategoryAccordion level={1} key={category.categoryId} category={category} />
                            ))}
                        </StyledWrapper>
                    )}
                    {!bargainBinCategories && !isLoadingCategories && (
                        <Notify type="info">{siteMessage("BargainBin_NoProductsMessage")}</Notify>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <LoadingOverlay loading={isLoadingBargains || isProductLoading}>
                        <BargainBinProductInfo />
                    </LoadingOverlay>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BargainBin),
    definition: {
        group: XNGAGE_ELEMENTS,
        displayName: "Bargain Bin",
    },
};

export default widgetModule;
